import { TrackingEvent } from '@caresend/types';
import { storeToRefs } from 'pinia';
import { defineComponent, computed, ref } from 'vue';
import __vue_component__ from '../../inputs/Checkbox.vue.js';
import __vue_component__$2 from '../portal/components/ItineraryWaypoint/components/ProcedureCharting/ChartingHospitalConfirm.vue.js';
import __vue_component__$1 from '../portal/components/ItineraryWaypoint/components/ProcedureCharting/ChartingTubeConfirm.vue.js';
import { usePortalStore } from '../portal/store/portal/index.js';
import { getChartingItemHighlightReferenceByIndexArray } from '../portal/store/portal/helpers/charting.js';
import { setProcedureChartingItems } from '../portal/store/portal/set/procedure.js';
import { trackEvent } from '../../../functions/tracking/tracking.js';
import { getStore } from '../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'ChartingChecklistItemCard',
    components: {
        Checkbox: __vue_component__,
        ChartingTubeConfirm: __vue_component__$1,
        ChartingHospitalConfirm: __vue_component__$2,
    },
    props: {
        procedureID: {
            type: String,
            required: true,
        },
        chartingItemIndexArray: {
            type: Array,
            required: true,
        },
        checkboxIndex: {
            type: Number,
            default: undefined,
        },
        checklistItemIndex: {
            type: Number,
            required: true,
        },
        highlightEnabled: Boolean,
        isTubeLabeling: {
            type: Boolean,
            default: false,
        },
        isHospitalDraw: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        var _a, _b;
        const store = getStore();
        const portalStore = usePortalStore();
        const { chartingHighlightItems } = storeToRefs(portalStore);
        const item = computed(() => portalStore.procedures.getters.getChartingChecklistItem(props.procedureID, props.chartingItemIndexArray, props.checkboxIndex));
        const highlight = computed(() => {
            var _a;
            if (!props.highlightEnabled)
                return false;
            const highlightItem = getChartingItemHighlightReferenceByIndexArray(chartingHighlightItems.value, props.chartingItemIndexArray);
            return !!((_a = highlightItem === null || highlightItem === void 0 ? void 0 : highlightItem.checklist) === null || _a === void 0 ? void 0 : _a[props.checklistItemIndex]);
        });
        /**
         * The special checkmarks that required a secondary confirmation are handled by the following refs:
         * - tubeLabelingModalIsOpen
         * - hospitalConfirmationModalIsOpen
         */
        const tubeLabelingModalIsOpen = ref(false);
        const hospitalConfirmationModalIsOpen = ref(false);
        /** Synced with the checkbox's checked state */
        const isChecked = ref((_b = (_a = item.value) === null || _a === void 0 ? void 0 : _a.checked) !== null && _b !== void 0 ? _b : false);
        const setCheckedValue = (value) => {
            if (!item.value)
                return;
            trackEvent(store, TrackingEvent.SET_CHARTING_CHECKBOX, {
                procedureID: props.procedureID,
                label: item.value.label,
            });
            const params = {
                procedureID: props.procedureID,
                newChartingChecklistItem: { checked: value },
                checklistItemIndex: props.checklistItemIndex,
                chartingItemIndexArray: props.chartingItemIndexArray,
            };
            setProcedureChartingItems(params, false);
        };
        /**
         * For the special checkmarks that required a secondary confirmation, do the following depending on confirmation:
         * - If the user does not confirm, revert the checkbox to unchecked
         * - If the user confirms, leave the button checked and set the charting item to checked
         */
        const handleSecondaryCheckConfirmation = (confirmed) => {
            if (!confirmed)
                isChecked.value = false;
            else
                setCheckedValue(true);
        };
        /**
         * This function is used to handle the check event of the checkbox.
         *
         * If:
         *  - the user checks the checkbox and it's not a special checkmark
         *  - OR the user unchecks the checkbox
         * Then:
         *  - set the charting item to checked
         * Else:
         *  - Open up the corresponding modal to ensure the user knows the importance of the checkmark
         */
        const handleCheckEvent = (value) => {
            if (value && props.isTubeLabeling) {
                tubeLabelingModalIsOpen.value = true;
            }
            else if (value && props.isHospitalDraw) {
                hospitalConfirmationModalIsOpen.value = true;
            }
            else {
                setCheckedValue(value);
            }
        };
        return {
            highlight,
            handleCheckEvent,
            handleSecondaryCheckConfirmation,
            hospitalConfirmationModalIsOpen,
            isChecked,
            item,
            tubeLabelingModalIsOpen,
        };
    },
});

export { script as default };
